/**
 * IoT/Hardware Icons Registry
 *
 * Provides IoT and embedded hardware icons from local SVG assets.
 * Uses explicit imports with SVGR for optimal tree-shaking.
 */

import type { ComponentType, ReactNode } from 'react';

// Explicit SVG imports with SVGR
import IotActuator from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-actuator.svg?react';
import IotAmqp from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-amqp.svg?react';
import IotAntenna from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-antenna.svg?react';
import IotBle from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-ble.svg?react';
import IotBluetooth from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-bluetooth.svg?react';
import IotBootloader from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-bootloader.svg?react';
import IotCanbus from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-canbus.svg?react';
import IotCtag from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-ctag.svg?react';
import IotEeprom from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-eeprom.svg?react';
import IotFirmware from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-firmware.svg?react';
import IotFlash from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-flash.svg?react';
import IotGpio from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-gpio.svg?react';
import IotI2c from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-i2c.svg?react';
import IotJtag from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-jtag.svg?react';
import IotLora from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-lora.svg?react';
import IotMcu from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-mcu.svg?react';
import IotMqtt from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-mqtt.svg?react';
import IotNfc from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-nfc.svg?react';
import IotPcb from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-pcb.svg?react';
import IotRfid from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-rfid.svg?react';
import IotSensor from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-sensor.svg?react';
import IotUart from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-uart.svg?react';
import IotWifi from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-wifi.svg?react';
import IotZigbee from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-zigbee.svg?react';
import IotZwave from '@/sections/attacks/attackPaths/components/icons/bundles/iot/iot-zwave.svg?react';
import type {
  IconDefinition,
  IconRenderer,
  IconRenderProps,
} from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { iconRenderers } from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { IOT_ICONS } from '@/sections/attacks/attackPaths/components/icons/types';

// ============================================================================
// Icon Component Type
// ============================================================================

type IotIconComponent = ComponentType<{
  className?: string;
  width?: string | number;
  height?: string | number;
}>;

// ============================================================================
// Fallback Component
// ============================================================================

const FallbackIcon = ({ className }: { className?: string }) => (
  <span className={className}>?</span>
);

// ============================================================================
// Icon Component Map
// ============================================================================

/**
 * Explicit mapping from IOT_ICONS names to SVGR components.
 */
const iotIconComponents: Partial<
  Record<(typeof IOT_ICONS)[number], IotIconComponent>
> = {
  Actuator: IotActuator,
  AMQP: IotAmqp,
  Antenna: IotAntenna,
  BLE: IotBle,
  Bluetooth: IotBluetooth,
  Bootloader: IotBootloader,
  CANBus: IotCanbus,
  CTAG: IotCtag,
  EEPROM: IotEeprom,
  Firmware: IotFirmware,
  Flash: IotFlash,
  GPIO: IotGpio,
  I2C: IotI2c,
  JTAG: IotJtag,
  Lora: IotLora,
  MCU: IotMcu,
  MQTT: IotMqtt,
  NFC: IotNfc,
  PCB: IotPcb,
  RFID: IotRfid,
  Sensor: IotSensor,
  UART: IotUart,
  WiFi: IotWifi,
  Zigbee: IotZigbee,
  ZWave: IotZwave,
};

/**
 * Human-readable labels for IoT icons.
 */
const iotIconLabels: Partial<Record<(typeof IOT_ICONS)[number], string>> = {
  Actuator: 'Actuator',
  AMQP: 'AMQP',
  Antenna: 'Antenna',
  BLE: 'Bluetooth LE',
  Bluetooth: 'Bluetooth',
  Bootloader: 'Bootloader',
  CANBus: 'CAN Bus',
  CTAG: 'CTAG',
  EEPROM: 'EEPROM',
  Firmware: 'Firmware',
  Flash: 'Flash Memory',
  GPIO: 'GPIO',
  I2C: 'I2C',
  JTAG: 'JTAG',
  Lora: 'LoRa',
  MCU: 'MCU',
  MQTT: 'MQTT',
  NFC: 'NFC',
  PCB: 'PCB',
  RFID: 'RFID',
  Sensor: 'Sensor',
  UART: 'UART',
  WiFi: 'WiFi',
  Zigbee: 'Zigbee',
  ZWave: 'Z-Wave',
};

// ============================================================================
// IoT Renderer Implementation
// ============================================================================

/**
 * IoT/Hardware icon renderer for the attack graph icon system.
 */
const iotRenderer: IconRenderer = {
  render: (
    name: string,
    { size = 24, className }: IconRenderProps
  ): ReactNode => {
    const IconComponent = iotIconComponents[name as (typeof IOT_ICONS)[number]];
    if (!IconComponent) return null;
    return <IconComponent className={className} width={size} height={size} />;
  },

  validate: (name: string): boolean => {
    return (IOT_ICONS as readonly string[]).includes(name);
  },

  getIcon: (name: string) => {
    const IconComponent = iotIconComponents[name as (typeof IOT_ICONS)[number]];
    if (!IconComponent) return null;

    return function WrappedIotIcon({
      size = 24,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      return <IconComponent className={className} width={size} height={size} />;
    };
  },

  getAll: (): IconDefinition[] =>
    IOT_ICONS.map(name => {
      const IconComponent = iotIconComponents[name];
      return {
        id: `iot:${name}`,
        component: IconComponent ?? FallbackIcon,
        label: iotIconLabels[name] ?? name,
        category: 'iot',
      };
    }),
};

// ============================================================================
// Register Renderer
// ============================================================================

iconRenderers.set('iot', iotRenderer);

export { iotRenderer };
